import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWishlist } from '../contexts/WishlistContext';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Heart, Trash2, ShoppingBag } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const Wishlist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, remove, loading } = useWishlist();

  const handleOpen = (item) => {
    const basePrice = item.price ? `₹${item.price}` : '';
    navigate('/design-studio', {
      state: {
        gender: item.gender || 'female',
        category: item.category || 'blouse',
        categoryName: item.category || '',
        productId: item.product_id,
        productName: item.name,
        productImage: item.image,
        basePrice,
      },
    });
  };

  return (
    <div data-testid="wishlist-page" className="min-h-screen pb-10" style={{ backgroundColor: SOFT, fontFamily: 'Outfit, sans-serif' }}>
      {/* Header */}
      <div className="sticky top-0 z-40 flex items-center gap-3 px-3 py-3 border-b" style={{ backgroundColor: 'rgba(250,249,246,0.96)', backdropFilter: 'blur(10px)', borderColor: LINE }}>
        <button data-testid="wishlist-back" onClick={() => navigate(-1)} className="p-1 -ml-1">
          <ChevronLeft className="w-5 h-5" style={{ color: INK }} strokeWidth={1.5} />
        </button>
        <div className="flex-1">
          <p className="text-[9px] tracking-[0.14em] uppercase" style={{ color: GOLD }}>Saved for you</p>
          <h1 className="text-[17px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>Wishlist</h1>
        </div>
        <span className="text-[11px]" style={{ color: MUTED }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Anon-login CTA */}
      {!user && (
        <div className="mx-3 mt-3 p-3 border" style={{ borderColor: GOLD, backgroundColor: 'rgba(212,175,55,0.05)' }}>
          <p className="text-[11px]" style={{ color: INK }}>Saved to this device only</p>
          <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>Sign in to sync your wishlist across devices</p>
          <button data-testid="wishlist-signin" onClick={() => navigate('/auth')}
            className="mt-2 px-3 py-1.5 text-[10px] tracking-[0.08em] uppercase text-white"
            style={{ backgroundColor: INK }}>
            Sign in
          </button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: GOLD, borderTopColor: 'transparent' }} />
        </div>
      ) : items.length === 0 ? (
        <div className="px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF', border: `1px solid ${LINE}` }}>
            <Heart className="w-6 h-6" style={{ color: MUTED }} strokeWidth={1.5} />
          </div>
          <p className="text-[14px]" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>No favorites yet</p>
          <p className="text-[11px] mt-1" style={{ color: MUTED }}>Tap the heart on any product to save it here</p>
          <button data-testid="wishlist-browse" onClick={() => navigate('/gender-selection')}
            className="mt-4 px-5 py-2.5 text-[11px] tracking-[0.08em] uppercase text-white"
            style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
            Start Browsing
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 p-3">
          {items.map(item => {
            const hasMrp = item.mrp && item.mrp > item.price && item.price > 0;
            return (
              <div key={item.id} data-testid={`wishlist-item-${item.product_id}`}
                className="bg-white border relative group" style={{ borderColor: LINE }}>
                <button data-testid={`wishlist-remove-${item.product_id}`} onClick={() => remove(item.product_id)}
                  className="absolute top-1.5 right-1.5 w-7 h-7 z-10 flex items-center justify-center bg-white/90"
                  style={{ backdropFilter: 'blur(6px)' }}>
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: '#991B1B' }} />
                </button>
                <button onClick={() => handleOpen(item)} className="w-full text-left">
                  <div className="aspect-[3/4] overflow-hidden bg-[#FAF9F6]">
                    {item.image ? (
                      <img src={item.image} alt={item.name} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ color: MUTED }}>
                        <Heart className="w-6 h-6" strokeWidth={1.5} />
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="text-[11px] truncate" style={{ color: INK, fontWeight: 500 }}>{item.name || 'Product'}</p>
                    {item.price > 0 ? (
                      <div className="flex items-baseline gap-1 mt-0.5">
                        <span className="text-[12px] font-medium" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>
                          ₹{Number(item.price).toLocaleString('en-IN')}
                        </span>
                        {hasMrp && (
                          <>
                            <span className="text-[9px] line-through" style={{ color: '#999' }}>₹{Number(item.mrp).toLocaleString('en-IN')}</span>
                            <span className="text-[8px]" style={{ color: '#166534' }}>
                              {Math.round((1 - item.price / item.mrp) * 100)}% off
                            </span>
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>Price on request</p>
                    )}
                    <p className="text-[8px] uppercase mt-1" style={{ color: '#BFBFBF' }}>{item.category}</p>
                  </div>
                </button>
                <button data-testid={`wishlist-customize-${item.product_id}`} onClick={() => handleOpen(item)}
                  className="w-full py-2 text-[10px] tracking-[0.08em] uppercase flex items-center justify-center gap-1 border-t"
                  style={{ color: INK, borderColor: LINE }}>
                  <ShoppingBag className="w-3 h-3" strokeWidth={1.5} /> Customize
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
